import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { resolveFileUrl } from "@/utils";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Building2, Shield, Pencil, Search, Loader2, Users, CreditCard, CheckCircle2, XCircle, PauseCircle, Clock } from "lucide-react";
import moment from "moment";
import { PLAN_LABELS, PLAN_COLORS, STATUS_LABELS, ROLE_LABELS, PLAN_FEATURES, MODULES } from "@/components/shared/PlanGuard";

const VENUE_TYPES = {
  restaurant: "Restaurant", bar: "Bar", cafeteria: "Cafeteria",
  takeaway: "Take Away", rostisseria: "Rostisseria",
  hotel_cuina: "Hotel amb Cuina", pastisseria: "Pastisseria / Forn", other: "Altres",
};

const STATUS_COLORS = {
  active:    "bg-emerald-100 text-emerald-700",
  trial:     "bg-blue-100 text-blue-700",
  suspended: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-500",
  expired:   "bg-orange-100 text-orange-700",
  inactive:  "bg-gray-100 text-gray-500",
};

const STATUS_ICONS = {
  active:    CheckCircle2,
  trial:     Clock,
  suspended: PauseCircle,
  cancelled: XCircle,
  expired:   XCircle,
  inactive:  XCircle,
};

export default function PlatformAdmin() {
  const { userRole, loading: venueLoading } = useVenue();
  const [venues, setVenues] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlan, setFilterPlan] = useState("all");
  const [editingSub, setEditingSub] = useState(null);
  const [editingVenue, setEditingVenue] = useState(null);
  const [subForm, setSubForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (userRole === "superadmin") loadData();
  }, [userRole]);

  const loadData = async () => {
    setLoading(true);
    const [v, s, m] = await Promise.all([
      hosys.entities.Venue.list(),
      hosys.entities.Subscription.list(),
      hosys.entities.VenueMember.list(),
    ]);
    setVenues(v);
    setSubscriptions(s);
    setMembers(m);
    setLoading(false);
  };

  const getSubForVenue = (venueId) => subscriptions.find(s => s.venue_id === venueId);
  const getMembersForVenue = (venueId) => members.filter(m => m.venue_id === venueId);

  const openEditSub = (venue) => {
    const sub = getSubForVenue(venue.id);
    setEditingVenue(venue);
    setSubForm({
      plan: sub?.plan || "basic",
      status: sub?.status || "trial",
      billing_cycle: sub?.billing_cycle || "yearly",
      start_date: sub?.start_date || moment().format("YYYY-MM-DD"),
      renewal_date: sub?.renewal_date || moment().add(1, "year").format("YYYY-MM-DD"),
      trial_ends_at: sub?.trial_ends_at || moment().add(30, "days").format("YYYY-MM-DD"),
      contact_person: sub?.contact_person || "",
      notes: sub?.notes || "",
    });
    setEditingSub(sub || null);
  };

  const handleSaveSub = async () => {
    setSaving(true);
    const historyEntry = {
      date: moment().format("YYYY-MM-DD"),
      plan: subForm.plan,
      status: subForm.status,
      note: subForm.notes || `Canvi a pla ${PLAN_LABELS[subForm.plan]}`,
    };
    if (editingSub) {
      await hosys.entities.Subscription.update(editingSub.id, {
        ...subForm,
        history: [...(editingSub.history || []), historyEntry],
      });
    } else {
      await hosys.entities.Subscription.create({
        venue_id: editingVenue.id,
        ...subForm,
        history: [historyEntry],
      });
    }
    setEditingVenue(null);
    setEditingSub(null);
    await loadData();
    setSaving(false);
  };

  if (venueLoading || loading) return <QuickLoader />;
  if (userRole !== "superadmin") {
    return <EmptyState icon={Shield} title="Accés restringit" description="Aquesta secció és exclusiva per a superadmins AEH Reus." />;
  }

  const getStatus = (v) => getSubForVenue(v.id)?.status || "trial";
  const getPlan   = (v) => getSubForVenue(v.id)?.plan   || "basic";

  const kpis = {
    total:        venues.length,
    active:       venues.filter(v => getStatus(v) === "active").length,
    trial:        venues.filter(v => getStatus(v) === "trial").length,
    suspended:    venues.filter(v => getStatus(v) === "suspended").length,
    basic:        venues.filter(v => getPlan(v) === "basic").length,
    professional: venues.filter(v => getPlan(v) === "professional").length,
    premium:      venues.filter(v => getPlan(v) === "premium").length,
  };

  const filtered = venues.filter(v => {
    const status = getStatus(v);
    const plan   = getPlan(v);
    if (search && !v.name.toLowerCase().includes(search.toLowerCase()) && !v.city?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus !== "all" && status !== filterStatus) return false;
    if (filterPlan !== "all" && plan !== filterPlan) return false;
    return true;
  });

  return (
    <div className="space-y-5">
      <PageHeader title="Plataforma AEH Reus" subtitle="Gestió global d'associats i subscripcions" />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="overview">Resum</TabsTrigger>
          <TabsTrigger value="venues">Locals ({venues.length})</TabsTrigger>
          <TabsTrigger value="plans">Plans i Mòduls</TabsTrigger>
        </TabsList>

        {/* ── OVERVIEW ── */}
        <TabsContent value="overview" className="space-y-4 pt-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiCard label="Total locals" value={kpis.total} color="text-gray-900" bg="bg-white" />
            <KpiCard label="Actius" value={kpis.active} color="text-emerald-600" bg="bg-emerald-50" />
            <KpiCard label="En prova" value={kpis.trial} color="text-blue-600" bg="bg-blue-50" />
            <KpiCard label="Suspesos" value={kpis.suspended} color="text-red-600" bg="bg-red-50" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <PlanKpi label="Pla Bàsic"        value={kpis.basic}        color="text-gray-700"   bg="bg-gray-50"   border="border-gray-100" />
            <PlanKpi label="Pla Professional" value={kpis.professional} color="text-purple-700" bg="bg-purple-50" border="border-purple-100" />
            <PlanKpi label="Pla Premium"      value={kpis.premium}      color="text-amber-700"  bg="bg-amber-50"  border="border-amber-100" />
          </div>

          {/* Renewals coming up */}
          <Card className="p-4 border-0 shadow-sm">
            <h3 className="text-sm font-bold text-gray-800 mb-3">Renovacions properes (30 dies)</h3>
            <div className="space-y-2">
              {subscriptions
                .filter(s => s.renewal_date && moment(s.renewal_date).isBetween(moment(), moment().add(30, "days")))
                .map(s => {
                  const v = venues.find(v => v.id === s.venue_id);
                  if (!v) return null;
                  return (
                    <div key={s.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <span className="text-sm font-medium text-gray-700">{v.name}</span>
                      <div className="flex items-center gap-2">
                        <Badge className={`${PLAN_COLORS[s.plan] || ""} border-0 text-[11px]`}>{PLAN_LABELS[s.plan]}</Badge>
                        <span className="text-xs text-gray-400">{moment(s.renewal_date).format("DD/MM/YYYY")}</span>
                      </div>
                    </div>
                  );
                })}
              {subscriptions.filter(s => s.renewal_date && moment(s.renewal_date).isBetween(moment(), moment().add(30, "days"))).length === 0 && (
                <p className="text-sm text-gray-400">Cap renovació en els propers 30 dies.</p>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* ── VENUES ── */}
        <TabsContent value="venues" className="space-y-3 pt-2">
          <Card className="p-4 border-0 shadow-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2 sm:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Cercar local..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger><SelectValue placeholder="Estat" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tots els estats</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterPlan} onValueChange={setFilterPlan}>
                <SelectTrigger><SelectValue placeholder="Pla" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tots els plans</SelectItem>
                  {Object.entries(PLAN_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>

          <p className="text-sm text-gray-500">{filtered.length} locals</p>

          <div className="space-y-2">
            {filtered.map(venue => {
              const sub    = getSubForVenue(venue.id);
              const status = sub?.status || "trial";
              const plan   = sub?.plan   || "basic";
              const venueMembers = getMembersForVenue(venue.id);
              const StatusIcon = STATUS_ICONS[status] || Clock;
              return (
                <Card key={venue.id} className="p-4 border-0 shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center flex-shrink-0">
                      {venue.logo_url
                        ? <img src={resolveFileUrl(venue.logo_url)} alt="" className="w-10 h-10 rounded-xl object-cover" />
                        : <Building2 className="w-5 h-5 text-[#7B2D3F]" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 text-sm">{venue.name}</p>
                        <Badge className={`${STATUS_COLORS[status]} border-0 text-[11px] flex items-center gap-1`}>
                          <StatusIcon className="w-3 h-3" />{STATUS_LABELS[status]}
                        </Badge>
                        <Badge className={`${PLAN_COLORS[plan] || ""} border-0 text-[11px]`}>{PLAN_LABELS[plan]}</Badge>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {VENUE_TYPES[venue.venue_type] || "—"}
                        {venue.city && ` · ${venue.city}`}
                        {sub?.renewal_date && ` · Renovació: ${moment(sub.renewal_date).format("DD/MM/YYYY")}`}
                        {` · ${venueMembers.length} usuaris`}
                      </p>
                      {sub?.contact_person && <p className="text-xs text-gray-400 mt-0.5">Contacte: {sub.contact_person}</p>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => openEditSub(venue)} className="text-[#7B2D3F] gap-1 flex-shrink-0">
                      <Pencil className="w-4 h-4" />
                      <span className="hidden sm:inline text-xs">Gestionar</span>
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* ── PLANS ── */}
        <TabsContent value="plans" className="space-y-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {["basic", "professional", "premium"].map(planKey => {
              const features = PLAN_FEATURES[planKey] || [];
              const allModules = Object.entries(MODULES);
              return (
                <Card key={planKey} className="p-5 border-0 shadow-sm">
                  <div className={`inline-flex px-3 py-1 rounded-full text-xs font-bold mb-3 ${PLAN_COLORS[planKey]}`}>
                    {PLAN_LABELS[planKey]}
                  </div>
                  <p className="text-xs text-gray-400 mb-3 font-medium uppercase tracking-wide">Mòduls inclosos</p>
                  <div className="space-y-1.5">
                    {allModules.map(([code, mod]) => {
                      const included = features.includes(code);
                      return (
                        <div key={code} className={`flex items-center gap-2 text-xs ${included ? "text-gray-700" : "text-gray-300"}`}>
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${included ? "bg-emerald-100" : "bg-gray-100"}`}>
                            {included
                              ? <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              : <XCircle className="w-3 h-3 text-gray-300" />
                            }
                          </div>
                          {mod.label}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Edit subscription dialog ── */}
      <Dialog open={!!editingVenue} onOpenChange={() => setEditingVenue(null)}>
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-0">
          <div className="overflow-y-auto flex-1">
            <div className="p-6 pb-2">
              <DialogHeader>
                <DialogTitle>Subscripció: {editingVenue?.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Pla</Label>
                <Select value={subForm.plan} onValueChange={v => setSubForm(f => ({ ...f, plan: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Bàsic</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estat</Label>
                <Select value={subForm.status} onValueChange={v => setSubForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Actiu</SelectItem>
                    <SelectItem value="trial">En prova</SelectItem>
                    <SelectItem value="suspended">Suspès</SelectItem>
                    <SelectItem value="cancelled">Cancel·lat</SelectItem>
                    <SelectItem value="expired">Expirat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Facturació</Label>
                <Select value={subForm.billing_cycle} onValueChange={v => setSubForm(f => ({ ...f, billing_cycle: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="yearly">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fi període prova</Label>
                <Input type="date" value={subForm.trial_ends_at} onChange={e => setSubForm(f => ({ ...f, trial_ends_at: e.target.value }))} />
              </div>
              <div>
                <Label>Data d'inici</Label>
                <Input type="date" value={subForm.start_date} onChange={e => setSubForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <Label>Data de renovació</Label>
                <Input type="date" value={subForm.renewal_date} onChange={e => setSubForm(f => ({ ...f, renewal_date: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Persona de contacte</Label>
              <Input value={subForm.contact_person} onChange={e => setSubForm(f => ({ ...f, contact_person: e.target.value }))} placeholder="Nom del responsable" />
            </div>
            <div>
              <Label>Notes internes</Label>
              <Textarea value={subForm.notes} onChange={e => setSubForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Notes per a l'AEH..." />
            </div>

            {/* Feature preview for selected plan */}
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs font-bold text-gray-600 mb-2">Mòduls del pla {PLAN_LABELS[subForm.plan]}:</p>
              <div className="flex flex-wrap gap-1">
                {(PLAN_FEATURES[subForm.plan] || []).map(code => (
                  <span key={code} className="text-[10px] bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                    {MODULES[code]?.label || code}
                  </span>
                ))}
              </div>
            </div>

            {/* History */}
            {editingSub?.history?.length > 0 && (
              <div>
                <Label className="text-xs text-gray-500">Historial de canvis</Label>
                <div className="mt-1 space-y-1 max-h-28 overflow-y-auto">
                  {[...editingSub.history].reverse().map((h, i) => (
                    <div key={`hazard-${i}`} className="text-xs text-gray-500 flex gap-2 py-1 border-b border-gray-50">
                      <span className="text-gray-400">{h.date}</span>
                      <span className="font-medium">{PLAN_LABELS[h.plan]}</span>
                      <span>·</span>
                      <span>{STATUS_LABELS[h.status]}</span>
                      {h.note && <span className="text-gray-400 italic truncate">{h.note}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
             </div>
            </div>
            </div>
            <DialogFooter className="p-6 pt-2 border-t">
            <Button variant="outline" onClick={() => setEditingVenue(null)}>Cancel·lar</Button>
            <Button onClick={handleSaveSub} disabled={saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
             {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
             Desar subscripció
            </Button>
            </DialogFooter>
            </DialogContent>
            </Dialog>
            </div>
            );
            }

function KpiCard({ label, value, color, bg }) {
  return (
    <div className={`${bg} rounded-xl p-4 shadow-sm`}>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-gray-500 font-medium mt-0.5">{label}</p>
    </div>
  );
}

function PlanKpi({ label, value, color, bg, border }) {
  return (
    <div className={`${bg} rounded-xl p-4 text-center border ${border}`}>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className={`text-xs ${color} opacity-70 font-medium mt-0.5`}>{label}</p>
    </div>
  );
}